import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Menu, X, MessageCircle, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { psychologistInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Главная', href: '/' },
    { label: 'Обо мне', href: '/about' },
    { label: 'Услуги', href: '/services' },
    { label: 'Запись', href: '/appointment' }
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="hidden lg:block bg-teal-700 text-sand-100 py-2.5">
        <div className="container mx-auto px-6 grid grid-cols-[auto_auto_1fr_auto] items-center gap-6 text-sm">
          <a href={`tel:${psychologistInfo.phone}`} className="flex items-center gap-2 whitespace-nowrap hover:text-white transition-colors">
            <Phone size={14} />
            <span>{psychologistInfo.phone}</span>
          </a>
          <a href={`mailto:${psychologistInfo.email}`} className="flex items-center gap-2 whitespace-nowrap hover:text-white transition-colors">
            <Mail size={14} />
            <span>{psychologistInfo.email}</span>
          </a>
          <div className="flex items-center justify-center gap-2 text-sand-50 whitespace-nowrap">
            <Globe size={14} className="text-sand-200" />
            <span>{psychologistInfo.onlineConsultationLabel}</span>
          </div>
          <div className="flex items-center justify-end gap-2 min-w-0 text-right">
            <MapPin size={14} className="text-sand-200" />
            <span className="truncate">{psychologistInfo.address}</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-sand-50/95 backdrop-blur-md shadow-lg' : 'bg-sand-50'
      }`}>
        <div className="container mx-auto px-6 border-b border-sand-200">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-teal-600 text-lg font-serif text-white shadow-lg transition-shadow group-hover:shadow-xl sm:hidden">
                ИЛ
              </div>
              <div className="hidden sm:flex w-12 h-12 rounded-full bg-teal-600 items-center justify-center text-white font-serif text-xl shadow-lg group-hover:shadow-xl transition-shadow">
                ИЛ
              </div>
              <div className="sm:hidden">
                <p className="font-serif text-xl text-sand-900 font-medium leading-tight">Ирина Лазарева</p>
              </div>
              <div className="hidden sm:block">
                <p className="font-serif text-lg text-sand-900 font-medium">Ирина Лазарева</p>
                <p className="text-xs text-sand-600 tracking-[0.14em] uppercase">Клинический психолог</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-4 py-2 font-medium text-sm transition-colors relative ${
                    isActive(item.href) 
                      ? 'text-teal-700' 
                      : 'text-sand-700 hover:text-teal-700'
                  }`}
                >
                  {item.label}
                  {isActive(item.href) && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-teal-600 rounded-full" />
                  )}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="https://t.me/IrinaAntonovnaLazareva"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 rounded-full bg-white text-sand-600 hover:bg-teal-50 hover:text-teal-700 transition-colors"
              >
                <MessageCircle size={20} />
              </a>
              <Link to="/appointment">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-full font-medium">
                  Записаться
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-sand-700 hover:text-teal-700 transition-colors"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-sand-50 border-t border-sand-200 px-6 py-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  isActive(item.href)
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-sand-700 hover:text-teal-700 hover:bg-teal-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-stone-100">
              <Link to="/appointment" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-full font-medium">
                  Записаться на консультацию
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
